import { Button, Group, Tooltip, VisuallyHidden } from '@mantine/core';
import React, { useId, useMemo, useState } from 'react';
import classes from '@/pages/Run/components/StepCardActions.module.css';
import AppIcon from '@/components/AppIcon.tsx';
import { HiArrowPathRoundedSquare } from 'react-icons/hi2';
import RerunStepModal from '@/components/RerunStepModal.tsx';
import { ActiveStatuses, ExecutionStatus } from '@/types/statuses.ts';
import { Run } from '@/types/runs.ts';
import { StepExecution, StepRun } from '@/types/stepExecutions.ts';
import { RERUN_STEP_CUTOFF } from '@/utils/constants.ts';
import { AutomationStep } from '@/types/automationSteps.ts';

type RerunStepModalProps = {
  run: Run;
  stepRun?: StepRun;
  stepExecution?: StepExecution;
  step?: AutomationStep;
  buttonText?: string;
  disabledMessage?: string;
};

const RerunStepButton = ({
  run,
  stepRun,
  stepExecution,
  step,
  buttonText,
  disabledMessage,
}: RerunStepModalProps) => {
  const [showRerun, setShowRerun] = useState(false);
  const disabledMessageId = useId();

  const disabledRerunMessage = useMemo(() => {
    if (disabledMessage) {
      return disabledMessage;
    } else if (stepRun?.rerun_eligibility?.allowed === false) {
      return stepRun.rerun_eligibility.reason ?? 'This result cannot be rerun.';
    } else if (!stepExecution) {
      return 'Step execution data is missing';
    } else if (run.status === ExecutionStatus.Cancelled) {
      return 'Run is cancelled. Create a new run to re-execute';
    } else if (ActiveStatuses.includes(run.status)) {
      return 'Run is active. Cancel the current execution or wait for it to complete before re-executing';
    } else {
      return undefined;
    }
  }, [disabledMessage, run.status, stepExecution, stepRun?.rerun_eligibility]);

  const runTooOld = useMemo(() => RERUN_STEP_CUTOFF >= new Date(run.created_at), [run.created_at]);

  return (
    <>
      {!runTooOld && (
        <Tooltip label={disabledRerunMessage} disabled={!disabledRerunMessage} openDelay={250}>
          <span style={{ display: 'inline-flex' }}>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowRerun(!showRerun);
              }}
              size={'xs'}
              variant={'default'}
              classNames={{ root: classes.button }}
              disabled={!!disabledRerunMessage}
              aria-describedby={disabledRerunMessage ? disabledMessageId : undefined}
            >
              <Group gap={'4px'} wrap={'nowrap'}>
                <AppIcon Icon={HiArrowPathRoundedSquare} size={16} />
                {buttonText ? buttonText : 'Rerun Step'}
              </Group>
            </Button>
          </span>
        </Tooltip>
      )}
      {disabledRerunMessage && <VisuallyHidden id={disabledMessageId}>{disabledRerunMessage}</VisuallyHidden>}

      {stepRun && !disabledRerunMessage && (
        <RerunStepModal
          run={run}
          stepRun={stepRun}
          step={step}
          opened={showRerun}
          setOpened={setShowRerun}
        />
      )}
    </>
  );
};

export default RerunStepButton;
